/*
 * DjVu decoder — our own implementation of the DjVu v3 format (Lizardtech
 * "DjVu Reference", November 2005) for /djvu-to-pdf.
 *
 * Why our own and not DjVu.js / DjVuLibre: every existing decoder is GPL, and
 * the PDF writer needs the page LAYERS (the JB2 symbol library and blits, the
 * IW44 background at its own resolution, the foreground colours), not a
 * rendered picture — that is what keeps the PDF as small as the DjVu.
 *
 * Where the published spec and real files disagree, this file follows the
 * files (checked pixel by pixel against pages rendered by the reference
 * implementation). Each such place is marked "spec differs":
 *   - ZP: z > code is the LPS branch (equality is MPS);
 *   - ZP has two pass-through variants: 0x8000 + a/2 (BZZ) and
 *     0x8000 + 3a/8 (IW44 signs and low mantissa bits);
 *   - JB2 numbers: decision 1 means "not below the cutoff" (phases as below);
 *   - IW44 activation value is step + step/2 - step/8, not 3/2 step, and the
 *     second mantissa bit carries a step/4 bias;
 *   - IW44 chroma is coded at half resolution when bit 0x80 of the delay
 *     byte is CLEAR;
 *   - TXT zones carry a relative text offset (the spec says "must be 0").
 *
 * Environment-neutral (page, worker, Node): no DOM, ES2019 syntax.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DjVuCore = api;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this), function () {
  'use strict';

  var VERSION = '1.0.0';

  function DjVuError(code, message) {
    var e = new Error(message || code);
    e.name = 'DjVuError';
    e.code = code;
    return e;
  }

  // ------------------------------------------------------------------
  // Z'-coder (ZP) — adaptive binary arithmetic decoder, spec appendix 3.
  // Table rows were read from the spec's table 9 and cross-checked against
  // an independent copy (251/251 equal after fixing six OCR glyph errors
  // of the PDF's text layer).
  // ------------------------------------------------------------------
  var ZP_P = new Uint16Array([
    0x8000, 0x8000, 0x8000, 0x6bbd, 0x6bbd, 0x5d45, 0x5d45, 0x51b9, 0x51b9, 0x4813, 0x4813, 0x3fd5,
    0x3fd5, 0x38b1, 0x38b1, 0x3275, 0x3275, 0x2cfd, 0x2cfd, 0x2825, 0x2825, 0x23ab, 0x23ab, 0x1f87,
    0x1f87, 0x1bbb, 0x1bbb, 0x1845, 0x1845, 0x1523, 0x1523, 0x1253, 0x1253, 0x0fcf, 0x0fcf, 0x0d95,
    0x0d95, 0x0b9d, 0x0b9d, 0x09e3, 0x09e3, 0x0861, 0x0861, 0x0711, 0x0711, 0x05f1, 0x05f1, 0x04f9,
    0x04f9, 0x0425, 0x0425, 0x0371, 0x0371, 0x02d9, 0x02d9, 0x0259, 0x0259, 0x01ed, 0x01ed, 0x0193,
    0x0193, 0x0149, 0x0149, 0x010b, 0x010b, 0x00d5, 0x00d5, 0x00a5, 0x00a5, 0x007b, 0x007b, 0x0057,
    0x0057, 0x003b, 0x003b, 0x0023, 0x0023, 0x0013, 0x0013, 0x0007, 0x0007, 0x0001, 0x0001, 0x5695,
    0x24ee, 0x8000, 0x0d30, 0x481a, 0x0481, 0x3579, 0x017a, 0x24ef, 0x007b, 0x1978, 0x0028, 0x10ca,
    0x000d, 0x0b5d, 0x0034, 0x078a, 0x00a0, 0x050f, 0x0117, 0x0358, 0x01ea, 0x0234, 0x0144, 0x0173,
    0x0234, 0x00f5, 0x0353, 0x00a1, 0x05c5, 0x011a, 0x03cf, 0x01aa, 0x0285, 0x0286, 0x01ab, 0x03d3,
    0x011a, 0x05c5, 0x00ba, 0x08ad, 0x007a, 0x0ccc, 0x01eb, 0x1302, 0x02e6, 0x1b81, 0x045e, 0x24ef,
    0x0690, 0x2865, 0x09de, 0x3987, 0x0dc8, 0x2c99, 0x10ca, 0x3b5f, 0x0b5d, 0x5695, 0x078a, 0x8000,
    0x050f, 0x24ee, 0x0358, 0x0d30, 0x0234, 0x0481, 0x0173, 0x017a, 0x00f5, 0x007b, 0x00a1, 0x0028,
    0x011a, 0x000d, 0x01aa, 0x0034, 0x0286, 0x00a0, 0x03d3, 0x0117, 0x05c5, 0x01ea, 0x08ad, 0x0144,
    0x0ccc, 0x0234, 0x1302, 0x0353, 0x1b81, 0x05c5, 0x24ef, 0x03cf, 0x2b74, 0x0285, 0x201d, 0x01ab,
    0x1715, 0x011a, 0x0fb7, 0x00ba, 0x0a67, 0x01eb, 0x06e7, 0x02e6, 0x0496, 0x045e, 0x030d, 0x0690,
    0x0206, 0x09de, 0x0155, 0x0dc8, 0x00e1, 0x2b74, 0x0094, 0x201d, 0x0188, 0x1715, 0x0252, 0x0fb7,
    0x0383, 0x0a67, 0x0547, 0x06e7, 0x07e2, 0x0496, 0x0bc0, 0x030d, 0x1178, 0x0206, 0x19da, 0x0155,
    0x24ef, 0x00e1, 0x320e, 0x0094, 0x432a, 0x0188, 0x447d, 0x0252, 0x5ece, 0x0383, 0x8000, 0x0547,
    0x481a, 0x07e2, 0x3579, 0x0bc0, 0x24ef, 0x1178, 0x1978, 0x19da, 0x2865, 0x24ef, 0x3987, 0x320e,
    0x2c99, 0x432a, 0x3b5f, 0x447d, 0x5695, 0x5ece, 0x8000, 0x8000, 0x5695, 0x481a, 0x481a
  ]);
  var ZP_M = new Uint16Array(251);
  (function () {
    var m = [
      0x0000, 0x0000, 0x0000, 0x10a5, 0x10a5, 0x1f28, 0x1f28, 0x2bd3, 0x2bd3, 0x36e3, 0x36e3, 0x408c,
      0x408c, 0x48fd, 0x48fd, 0x505d, 0x505d, 0x56d0, 0x56d0, 0x5c71, 0x5c71, 0x615b, 0x615b, 0x65a5,
      0x65a5, 0x6962, 0x6962, 0x6ca2, 0x6ca2, 0x6f74, 0x6f74, 0x71e6, 0x71e6, 0x7404, 0x7404, 0x75d6,
      0x75d6, 0x7768, 0x7768, 0x78c2, 0x78c2, 0x79ea, 0x79ea, 0x7ae7, 0x7ae7, 0x7bbe, 0x7bbe, 0x7c75,
      0x7c75, 0x7d0f, 0x7d0f, 0x7d91, 0x7d91, 0x7dfe, 0x7dfe, 0x7e5a, 0x7e5a, 0x7ea6, 0x7ea6, 0x7ee6,
      0x7ee6, 0x7f1a, 0x7f1a, 0x7f45, 0x7f45, 0x7f6b, 0x7f6b, 0x7f8d, 0x7f8d, 0x7faa, 0x7faa, 0x7fc3,
      0x7fc3, 0x7fd7, 0x7fd7, 0x7fe7, 0x7fe7, 0x7ff2, 0x7ff2, 0x7ffa, 0x7ffa, 0x7fff, 0x7fff
    ];
    // States 83..250 have theta = 0: an MPS there always moves the state.
    for (var i = 0; i < m.length; i++) ZP_M[i] = m[i];
  })();
  var ZP_UP = new Uint8Array([
    84, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13,
    14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
    26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37,
    38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49,
    50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61,
    62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73,
    74, 75, 76, 77, 78, 79, 80, 81, 82, 81, 82, 9,
    86, 5, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97,
    82, 99, 76, 101, 70, 103, 66, 105, 106, 107, 66, 109,
    60, 111, 56, 69, 114, 65, 116, 61, 118, 57, 120, 53,
    122, 49, 124, 43, 72, 39, 60, 33, 56, 29, 52, 23,
    48, 23, 42, 137, 38, 21, 140, 15, 142, 9, 144, 141,
    146, 147, 148, 149, 150, 151, 152, 153, 154, 155, 70, 157,
    66, 81, 62, 75, 58, 69, 54, 65, 50, 167, 44, 65,
    40, 59, 34, 55, 30, 175, 24, 177, 178, 179, 180, 181,
    182, 183, 184, 69, 186, 59, 188, 55, 190, 51, 192, 47,
    194, 41, 196, 37, 198, 199, 72, 201, 62, 203, 58, 205,
    54, 207, 50, 209, 46, 211, 40, 213, 36, 215, 30, 217,
    26, 219, 20, 71, 14, 61, 14, 57, 8, 53, 228, 49,
    230, 45, 232, 39, 234, 35, 138, 29, 24, 25, 240, 19,
    22, 13, 16, 13, 10, 7, 244, 249, 10, 89, 230
  ]);
  var ZP_DN = new Uint8Array([
    145, 4, 3, 1, 2, 3, 4, 5, 6, 7, 8, 9,
    10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
    22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33,
    34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45,
    46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57,
    58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69,
    70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80, 85,
    226, 6, 176, 143, 138, 141, 112, 135, 104, 133, 100, 129,
    98, 127, 72, 125, 102, 123, 60, 121, 110, 119, 108, 117,
    54, 115, 48, 113, 134, 59, 132, 55, 130, 51, 128, 47,
    126, 41, 62, 37, 66, 31, 54, 25, 50, 131, 46, 17,
    40, 15, 136, 7, 32, 139, 172, 9, 170, 85, 168, 248,
    166, 247, 164, 197, 162, 95, 160, 173, 158, 165, 156, 161,
    60, 159, 56, 71, 52, 163, 48, 59, 42, 171, 38, 169,
    32, 53, 26, 47, 174, 193, 18, 191, 222, 189, 218, 187,
    216, 185, 214, 61, 212, 53, 210, 49, 208, 45, 206, 39,
    204, 195, 202, 31, 200, 243, 64, 239, 56, 237, 52, 235,
    48, 233, 44, 231, 38, 229, 34, 227, 28, 225, 22, 223,
    16, 221, 220, 63, 8, 55, 224, 51, 2, 47, 87, 43,
    246, 37, 244, 33, 238, 27, 236, 21, 16, 15, 8, 241,
    242, 7, 10, 245, 2, 1, 83, 250, 2, 143, 246
  ]);

  // Leading 1-bits of a byte, for renormalisation after an LPS.
  var FFZT = new Uint8Array(256);
  (function () {
    for (var i = 0; i < 256; i++) {
      var n = 0;
      for (var j = i; j & 0x80; j = (j << 1) & 0xff) n++;
      FFZT[i] = n;
    }
  })();

  // A stream that ends early is padded with 1-bits (spec 12.1). A decoder
  // that keeps asking far past the end is reading a corrupt chunk: stop it
  // instead of producing an endless page of garbage.
  var ZP_OVERRUN_LIMIT = 64;

  function ZPDecoder(buf, start, end) {
    this.buf = buf;
    this.pos = start;
    this.end = end;
    this.overrun = 0;
    this.a = 0;
    var b0 = this.nextByte();
    var b1 = this.nextByte();
    this.code = (b0 << 8) | b1;
    this.buffer = 0;
    this.scount = 0;
    this.preload();
    this.fence = this.code >= 0x8000 ? 0x7fff : this.code;
  }
  ZPDecoder.prototype.nextByte = function () {
    if (this.pos < this.end) return this.buf[this.pos++];
    if (++this.overrun > ZP_OVERRUN_LIMIT) throw DjVuError('ZP_EOF', 'arithmetic stream ended');
    return 0xff;
  };
  ZPDecoder.prototype.preload = function () {
    while (this.scount <= 24) {
      this.buffer = (this.buffer << 8) | this.nextByte();
      this.scount += 8;
    }
  };
  ZPDecoder.prototype.ffz = function (x) {
    return x >= 0xff00 ? FFZT[x & 0xff] + 8 : FFZT[(x >> 8) & 0xff];
  };
  // Decode one decision with adaptive context ctx[n] (a Uint8Array cell).
  ZPDecoder.prototype.decode = function (ctx, n) {
    var st = ctx[n];
    var z = this.a + ZP_P[st];
    if (z <= this.fence) { this.a = z; return st & 1; }
    var bit = st & 1;
    var d = 0x6000 + ((z + this.a) >> 2);
    if (z > d) z = d;
    if (z > this.code) { // spec differs: equality belongs to the MPS branch
      z = 0x10000 - z;
      this.a += z;
      this.code += z;
      ctx[n] = ZP_DN[st];
      var shift = this.ffz(this.a);
      this.scount -= shift;
      this.a = (this.a << shift) & 0xffff;
      this.code = ((this.code << shift) & 0xffff) | ((this.buffer >>> this.scount) & ((1 << shift) - 1));
      if (this.scount < 16) this.preload();
      this.fence = this.code >= 0x8000 ? 0x7fff : this.code;
      return bit ^ 1;
    }
    if (this.a >= ZP_M[st]) ctx[n] = ZP_UP[st];
    this.scount -= 1;
    this.a = (z << 1) & 0xffff;
    this.code = ((this.code << 1) & 0xffff) | ((this.buffer >>> this.scount) & 1);
    if (this.scount < 16) this.preload();
    this.fence = this.code >= 0x8000 ? 0x7fff : this.code;
    return bit;
  };
  // Pass-through decision (no context); the MPS is 0.
  ZPDecoder.prototype.decodeRaw = function (z) {
    if (z > this.code) {
      z = 0x10000 - z;
      this.a += z;
      this.code += z;
      var shift = this.ffz(this.a);
      this.scount -= shift;
      this.a = (this.a << shift) & 0xffff;
      this.code = ((this.code << shift) & 0xffff) | ((this.buffer >>> this.scount) & ((1 << shift) - 1));
      if (this.scount < 16) this.preload();
      this.fence = this.code >= 0x8000 ? 0x7fff : this.code;
      return 1;
    }
    this.scount -= 1;
    this.a = (z << 1) & 0xffff;
    this.code = ((this.code << 1) & 0xffff) | ((this.buffer >>> this.scount) & 1);
    if (this.scount < 16) this.preload();
    this.fence = this.code >= 0x8000 ? 0x7fff : this.code;
    return 0;
  };
  // spec differs: BZZ's pass-through halves the interval...
  ZPDecoder.prototype.decodeBit = function () {
    return this.decodeRaw(0x8000 + (this.a >> 1));
  };
  // ...while IW44's uses 3/8 (the spec's figure 2 describes only this one).
  ZPDecoder.prototype.decodeIW = function () {
    return this.decodeRaw(0x8000 + ((this.a + this.a + this.a) >> 3));
  };

  // ------------------------------------------------------------------
  // BZZ — Burrows-Wheeler + adaptive MTF over the ZP coder (appendix 4).
  // ------------------------------------------------------------------
  var BZZ_MAX_BLOCK = 4096 * 1024;
  var BZZ_MAX_OUTPUT = 256 * 1024 * 1024;

  function bzzBinary(zp, ctx, off, bits) {
    var n = 1, m = 1 << bits;
    while (n < m) n = (n << 1) | zp.decode(ctx, off + n - 1);
    return n - m;
  }

  function bzzBlock(zp, ctx, size) {
    var fshift = 0;
    if (zp.decodeBit()) { fshift++; if (zp.decodeBit()) fshift++; }
    var mtf = new Uint8Array(256);
    for (var i = 0; i < 256; i++) mtf[i] = i;
    // freq values can pass 2^31 before the overflow rescale: keep them as doubles.
    var freq = [0, 0, 0, 0];
    var fadd = 4;
    var mtfno = 3;
    var markerpos = -1;
    var data = new Uint8Array(size);
    for (i = 0; i < size; i++) {
      var ctxid = mtfno < 2 ? mtfno : 2;
      if (zp.decode(ctx, ctxid)) mtfno = 0;
      else if (zp.decode(ctx, 3 + ctxid)) mtfno = 1;
      else if (zp.decode(ctx, 6)) mtfno = 2 + bzzBinary(zp, ctx, 7, 1);
      else if (zp.decode(ctx, 8)) mtfno = 4 + bzzBinary(zp, ctx, 9, 2);
      else if (zp.decode(ctx, 12)) mtfno = 8 + bzzBinary(zp, ctx, 13, 3);
      else if (zp.decode(ctx, 20)) mtfno = 16 + bzzBinary(zp, ctx, 21, 4);
      else if (zp.decode(ctx, 36)) mtfno = 32 + bzzBinary(zp, ctx, 37, 5);
      else if (zp.decode(ctx, 68)) mtfno = 64 + bzzBinary(zp, ctx, 69, 6);
      else if (zp.decode(ctx, 132)) mtfno = 128 + bzzBinary(zp, ctx, 133, 7);
      else { mtfno = 256; data[i] = 0; markerpos = i; continue; }
      var c = mtf[mtfno];
      data[i] = c;
      fadd = fadd + (fadd >> fshift);
      if (fadd > 0x10000000) {
        fadd = fadd >> 24;
        for (var f = 0; f < 4; f++) freq[f] = Math.floor(freq[f] / 16777216);
      }
      var fc = fadd;
      if (mtfno < 4) fc += freq[mtfno];
      var k = mtfno;
      for (; k >= 4; k--) mtf[k] = mtf[k - 1];
      for (; k > 0 && fc >= freq[k - 1]; k--) {
        mtf[k] = mtf[k - 1];
        freq[k] = freq[k - 1];
      }
      mtf[k] = c;
      freq[k] = fc;
    }
    if (markerpos < 1 || markerpos >= size) throw DjVuError('BZZ_CORRUPT', 'block without end marker');
    // Inverse Burrows-Wheeler transform.
    var posn = new Uint32Array(size);
    var count = new Int32Array(256);
    for (i = 0; i < size; i++) {
      if (i === markerpos) continue;
      var ch = data[i];
      posn[i] = ((ch << 24) | (count[ch] & 0xffffff)) >>> 0;
      count[ch]++;
    }
    var last = 1;
    for (i = 0; i < 256; i++) { var t = count[i]; count[i] = last; last += t; }
    var out = new Uint8Array(size - 1);
    i = 0;
    last = size - 1;
    while (last > 0) {
      var nn = posn[i];
      var cc = nn >>> 24;
      out[--last] = cc;
      i = count[cc] + (nn & 0xffffff);
    }
    if (i !== markerpos) throw DjVuError('BZZ_CORRUPT', 'broken block permutation');
    return out;
  }

  function bzzDecode(buf, start, end) {
    var zp = new ZPDecoder(buf, start, end);
    var ctx = new Uint8Array(300);
    var parts = [], total = 0;
    for (;;) {
      var n = 1;
      while (n < 0x1000000) n = (n << 1) | zp.decodeBit();
      var size = n - 0x1000000;
      if (size === 0) break;
      if (size > BZZ_MAX_BLOCK) throw DjVuError('BZZ_CORRUPT', 'block too large');
      var block = bzzBlock(zp, ctx, size);
      parts.push(block);
      total += block.length;
      if (total > BZZ_MAX_OUTPUT) throw DjVuError('BZZ_CORRUPT', 'output too large');
    }
    if (parts.length === 1) return parts[0];
    var out = new Uint8Array(total), p = 0;
    for (var i = 0; i < parts.length; i++) { out.set(parts[i], p); p += parts[i].length; }
    return out;
  }

  // ------------------------------------------------------------------
  // Byte helpers and IFF85 chunks.
  // ------------------------------------------------------------------
  function u16(b, p) { return (b[p] << 8) | b[p + 1]; }
  function u24(b, p) { return (b[p] << 16) | (b[p + 1] << 8) | b[p + 2]; }
  function u32(b, p) { return ((b[p] << 24) >>> 0) + ((b[p + 1] << 16) | (b[p + 2] << 8) | b[p + 3]); }
  function fourcc(b, p) { return String.fromCharCode(b[p], b[p + 1], b[p + 2], b[p + 3]); }

  var utf8Decoder = typeof TextDecoder !== 'undefined' ? new TextDecoder('utf-8') : null;
  function utf8(bytes) {
    if (utf8Decoder) return utf8Decoder.decode(bytes);
    var s = '';
    for (var i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    try { return decodeURIComponent(escape(s)); } catch (e) { return s; }
  }

  // Children of the chunk whose data spans [start, end). A declared size that
  // runs past the end (a truncated download) is clipped and flagged.
  function listChunks(b, start, end) {
    var out = [];
    var p = start;
    while (p + 8 <= end) {
      var id = fourcc(b, p);
      var size = u32(b, p + 4);
      var dataStart = p + 8;
      var dataEnd = dataStart + size;
      var truncated = false;
      if (dataEnd > end) { dataEnd = end; truncated = true; }
      var ch = { id: id, start: dataStart, end: dataEnd, truncated: truncated };
      if (id === 'FORM' && dataEnd - dataStart >= 4) {
        ch.form = fourcc(b, dataStart);
        ch.children = listChunks(b, dataStart + 4, dataEnd);
      }
      out.push(ch);
      p = dataStart + size;
      if (p & 1) p++;
    }
    return out;
  }

  // The FORM chunk at the head of a component (with or without "AT&T").
  function openForm(b) {
    var p = 0;
    if (b.length >= 4 && b[0] === 0x41 && b[1] === 0x54 && b[2] === 0x26 && b[3] === 0x54) p = 4;
    if (b.length < p + 12 || fourcc(b, p) !== 'FORM') throw DjVuError('NOT_DJVU', 'not a DjVu file');
    var size = u32(b, p + 4);
    var end = p + 8 + size;
    var truncated = false;
    if (end > b.length) { end = b.length; truncated = true; }
    return {
      form: fourcc(b, p + 8),
      offset: p,
      end: end,
      declaredEnd: p + 8 + size,
      truncated: truncated,
      chunks: listChunks(b, p + 12, end)
    };
  }

  // ------------------------------------------------------------------
  // Document directory (DIRM) and outline (NAVM).
  // ------------------------------------------------------------------
  var FILE_INCLUDE = 0, FILE_PAGE = 1, FILE_THUMBNAILS = 2, FILE_SHARED_ANNO = 3;

  function zstrReader(z) {
    var p = 0;
    return {
      next: function () {
        var s = p;
        while (p < z.length && z[p] !== 0) p++;
        var out = utf8(z.subarray(s, p));
        if (p < z.length) p++;
        return out;
      },
      pos: function () { return p; },
      seek: function (q) { p = q; }
    };
  }

  function decodeDirm(b, start, end) {
    var flags = b[start];
    var bundled = (flags & 0x80) !== 0;
    var version = flags & 0x7f;
    var n = u16(b, start + 1);
    var p = start + 3;
    var offsets = [];
    if (bundled) {
      for (var i = 0; i < n; i++) { offsets.push(u32(b, p)); p += 4; }
    }
    var z = bzzDecode(b, p, end);
    var q = 0;
    var files = [];
    for (i = 0; i < n; i++) { files.push({ size: u24(z, q) }); q += 3; }
    for (i = 0; i < n; i++) {
      var f = z[q++];
      var type, hasName, hasTitle;
      if (version === 0) {
        type = (f & 1) ? FILE_PAGE : FILE_INCLUDE;
        hasName = (f & 2) !== 0;
        hasTitle = (f & 4) !== 0;
      } else {
        type = f & 0x3f;
        hasName = (f & 0x80) !== 0;
        hasTitle = (f & 0x40) !== 0;
      }
      files[i].type = type;
      files[i].hasName = hasName;
      files[i].hasTitle = hasTitle;
      if (bundled) files[i].offset = offsets[i];
    }
    var rd = zstrReader(z.subarray(q));
    for (i = 0; i < n; i++) {
      files[i].id = rd.next();
      files[i].name = files[i].hasName ? rd.next() : files[i].id;
      files[i].title = files[i].hasTitle ? rd.next() : files[i].id;
    }
    return { bundled: bundled, version: version, files: files };
  }

  function decodeNavm(b, start, end) {
    var z = bzzDecode(b, start, end);
    if (z.length < 2) return [];
    var count = u16(z, 0);
    var p = 2, made = 0;
    function rec(depth) {
      if (p + 7 > z.length || depth > 64) throw DjVuError('NAVM_CORRUPT', 'outline truncated');
      var nChildren = z[p++];
      var nd = u24(z, p); p += 3;
      var title = utf8(z.subarray(p, p + nd)); p += nd;
      var nu = u24(z, p); p += 3;
      var url = utf8(z.subarray(p, p + nu)); p += nu;
      made++;
      var node = { title: title, url: url, children: [] };
      for (var i = 0; i < nChildren; i++) node.children.push(rec(depth + 1));
      return node;
    }
    var roots = [];
    while (made < count && p < z.length) roots.push(rec(0));
    return roots;
  }

  // Reads the directory from the head of the main file. With a partial head
  // it answers how many bytes it still needs instead of guessing.
  function readDirectory(head, totalSize) {
    var p = 0;
    if (head.length >= 4 && head[0] === 0x41 && head[1] === 0x54 && head[2] === 0x26 && head[3] === 0x54) p = 4;
    if (head.length < p + 16) return { needBytes: p + 16 };
    if (fourcc(head, p) !== 'FORM') throw DjVuError('NOT_DJVU', 'not a DjVu file');
    var formSize = u32(head, p + 4);
    var form = fourcc(head, p + 8);
    var fileEnd = p + 8 + formSize;
    var total = typeof totalSize === 'number' ? totalSize : head.length;
    var truncated = fileEnd > total;
    if (form === 'DJVU') {
      return {
        kind: 'single', truncated: truncated,
        files: [{ id: '', name: '', title: '', type: FILE_PAGE, offset: p, size: Math.min(fileEnd, total) - p }],
        pages: [0], outline: []
      };
    }
    if (form !== 'DJVM') throw DjVuError('UNSUPPORTED_FORM', 'unsupported DjVu component: ' + form);
    var q = p + 12;
    if (head.length < q + 8) return { needBytes: q + 8 };
    if (fourcc(head, q) !== 'DIRM') throw DjVuError('NO_DIRECTORY', 'multipage document without directory');
    var dirmSize = u32(head, q + 4);
    var dirmEnd = q + 8 + dirmSize;
    if (head.length < dirmEnd) return { needBytes: dirmEnd + 64 };
    var dir = decodeDirm(head, q + 8, dirmEnd);
    var outline = [];
    var r = dirmEnd + (dirmEnd & 1);
    // An outline, when present, follows the directory immediately.
    if (r + 8 > head.length && r + 8 <= Math.min(total, fileEnd)) return { needBytes: r + 8 + 64 };
    if (r + 8 <= head.length && fourcc(head, r) === 'NAVM') {
      var navSize = u32(head, r + 4);
      if (head.length < r + 8 + navSize) return { needBytes: r + 8 + navSize };
      try { outline = decodeNavm(head, r + 8, r + 8 + navSize); }
      catch (e) { outline = []; }
    }
    var pages = [];
    for (var i = 0; i < dir.files.length; i++) if (dir.files[i].type === FILE_PAGE) pages.push(i);
    return {
      kind: dir.bundled ? 'bundled' : 'indirect',
      truncated: truncated,
      files: dir.files,
      pages: pages,
      outline: outline
    };
  }

  // "#p0001.djvu", "#12", "#+1", a page title or an external URL.
  function resolveUrl(url, directory, fromPage) {
    if (!url) return null;
    if (url.charAt(0) !== '#') return { external: url };
    var s = url.slice(1);
    var pages = directory.pages;
    if (/^[+-]\d+$/.test(s)) {
      var rel = (fromPage || 0) + parseInt(s, 10);
      return rel >= 0 && rel < pages.length ? { page: rel } : null;
    }
    if (/^\d+$/.test(s)) {
      var n = parseInt(s, 10) - 1;
      if (n >= 0 && n < pages.length) return { page: n };
    }
    for (var i = 0; i < pages.length; i++) {
      var f = directory.files[pages[i]];
      if (f.id === s || f.name === s) return { page: i };
    }
    for (i = 0; i < pages.length; i++) {
      if (directory.files[pages[i]].title === s) return { page: i };
    }
    return null;
  }

  // ------------------------------------------------------------------
  // INFO chunk.
  // ------------------------------------------------------------------
  function decodeInfo(b, start, end) {
    var len = end - start;
    var info = { width: 0, height: 0, minor: 0, major: 0, dpi: 300, gamma: 2.2, rotation: 0, flags: 0 };
    if (len >= 4) { info.width = u16(b, start); info.height = u16(b, start + 2); }
    if (len >= 5) info.minor = b[start + 4];
    if (len >= 6) info.major = b[start + 5];
    if (len >= 8) info.dpi = b[start + 6] | (b[start + 7] << 8); // little-endian (sic)
    if (len >= 9) info.gamma = b[start + 8] / 10;
    if (len >= 10) info.flags = b[start + 9];
    if (info.dpi < 25 || info.dpi > 6000) info.dpi = 300;
    if (info.gamma < 0.3 || info.gamma > 5) info.gamma = 2.2;
    // Clockwise degrees the page must be turned to be read upright.
    switch (info.flags & 7) {
      case 6: info.rotation = 270; break; // 90 degrees counter-clockwise
      case 2: info.rotation = 180; break;
      case 5: info.rotation = 90; break;
      default: info.rotation = 0;
    }
    return info;
  }

  // ------------------------------------------------------------------
  // JB2 — the bilevel mask (appendix 2).
  // Bitmaps are kept bottom-up like the format itself: row 0 is the bottom.
  // ------------------------------------------------------------------
  var BORDER = 4;
  var BIGPOS = 262142, BIGNEG = -262143;
  var NC_COMMENT_BYTE = 0, NC_COMMENT_LEN = 1, NC_REC_TYPE = 2, NC_MATCH_INDEX = 3,
    NC_ABS_LOC_X = 4, NC_ABS_LOC_Y = 5, NC_ABS_SIZE_X = 6, NC_ABS_SIZE_Y = 7,
    NC_IMAGE_SIZE = 8, NC_INHERITED = 9, NC_REL_LOC_X_CUR = 10, NC_REL_LOC_X_LAST = 11,
    NC_REL_LOC_Y_CUR = 12, NC_REL_LOC_Y_LAST = 13, NC_REL_SIZE_X = 14, NC_REL_SIZE_Y = 15;
  var JB2_MAX_CELLS = 8 * 1024 * 1024;
  var JB2_MAX_RECORDS = 4 * 1024 * 1024;
  var JB2_MAX_PIXELS = 120 * 1000 * 1000;

  function Bitmap(w, h) {
    this.w = w;
    this.h = h;
    this.stride = w + 2 * BORDER;
    this.data = new Uint8Array((h + 2 * BORDER) * this.stride);
  }
  Bitmap.prototype.row = function (y) { return (y + BORDER) * this.stride + BORDER; };

  var shapeSerial = 0;
  // Tight black-pixel box of a decoded bitmap as a compact shape (bottom-up,
  // one byte per pixel) plus where that box sits inside the bitmap. The
  // library keeps only such trimmed shapes; spec 11.5.10 codes locations and
  // refinements against the trimmed box.
  function trim(bm) {
    var w = bm.w, h = bm.h, d = bm.data;
    var left = w, right = -1, bottom = h, top = -1;
    for (var y = 0; y < h; y++) {
      var r = bm.row(y);
      for (var x = 0; x < w; x++) {
        if (d[r + x]) {
          if (x < left) left = x;
          if (x > right) right = x;
          if (y < bottom) bottom = y;
          if (y > top) top = y;
        }
      }
    }
    if (right < 0) return { shape: { id: ++shapeSerial, w: 0, h: 0, bits: new Uint8Array(0) }, dx: 0, dy: 0 };
    var sw = right - left + 1, sh = top - bottom + 1;
    var bits = new Uint8Array(sw * sh);
    for (y = 0; y < sh; y++) {
      var src = bm.row(bottom + y) + left;
      bits.set(d.subarray(src, src + sw), y * sw);
    }
    return { shape: { id: ++shapeSerial, w: sw, h: sh, bits: bits }, dx: left, dy: bottom };
  }

  function JB2Decoder(buf, start, end, opts) {
    this.zp = new ZPDecoder(buf, start, end);
    this.opts = opts || {};
    this.bitdist = new Uint8Array(1024);
    this.cbitdist = new Uint8Array(2048);
    this.offsetTypeCtx = new Uint8Array(1);
    this.refineCtx = new Uint8Array(1);
    this.resetNumcoder();
    this.lib = [];
    this.blits = [];      // [shape, left, bottom] triples, trimmed boxes, 0-based
    this.width = 0;
    this.height = 0;
    this.gotStart = false;
    this.pixels = 0;
  }
  JB2Decoder.prototype.resetNumcoder = function () {
    this.roots = new Int32Array(16);
    this.cap = 4096;
    this.cells = new Uint8Array(this.cap);
    this.left = new Int32Array(this.cap);
    this.right = new Int32Array(this.cap);
    this.ncell = 1;
  };
  JB2Decoder.prototype.grow = function () {
    var cap = this.cap * 2;
    if (cap > JB2_MAX_CELLS) throw DjVuError('JB2_CORRUPT', 'too many number contexts');
    var c = new Uint8Array(cap); c.set(this.cells); this.cells = c;
    var l = new Int32Array(cap); l.set(this.left); this.left = l;
    var r = new Int32Array(cap); r.set(this.right); this.right = r;
    this.cap = cap;
  };
  // Multivalue integer in [low, high] through a lazily grown binary tree of
  // contexts. spec differs: decision 1 means "value >= cutoff".
  JB2Decoder.prototype.num = function (low, high, root) {
    var negative = false, cutoff = 0, phase = 1, range = -1;
    var slotKind = 0, slot = root; // 0 = roots, 1 = left, 2 = right
    while (range !== 1) {
      var cell = slotKind === 0 ? this.roots[slot] : (slotKind === 1 ? this.left[slot] : this.right[slot]);
      if (cell === 0) {
        cell = this.ncell++;
        if (cell >= this.cap) this.grow();
        this.cells[cell] = 0;
        this.left[cell] = 0;
        this.right[cell] = 0;
        if (slotKind === 0) this.roots[slot] = cell;
        else if (slotKind === 1) this.left[slot] = cell;
        else this.right[slot] = cell;
      }
      var decision = (low >= cutoff) || ((high >= cutoff) && this.zp.decode(this.cells, cell) !== 0);
      slotKind = decision ? 2 : 1;
      slot = cell;
      if (phase === 1) {
        negative = !decision;
        if (negative) { var t = -low - 1; low = -high - 1; high = t; }
        phase = 2;
        cutoff = 1;
      } else if (phase === 2) {
        if (!decision) {
          phase = 3;
          range = (cutoff + 1) >> 1;
          if (range === 1) cutoff = 0;
          else cutoff -= range >> 1;
        } else {
          cutoff += cutoff + 1;
          if (cutoff > 0x3fffffff) throw DjVuError('JB2_CORRUPT', 'number out of range');
        }
      } else {
        range >>= 1;
        if (range !== 1) {
          if (!decision) cutoff -= range >> 1;
          else cutoff += range >> 1;
        } else if (!decision) {
          cutoff--;
        }
      }
    }
    return negative ? (-cutoff - 1) : cutoff;
  };
  JB2Decoder.prototype.budget = function (w, h) {
    this.pixels += w * h;
    if (this.pixels > JB2_MAX_PIXELS) throw DjVuError('JB2_CORRUPT', 'mask larger than any page');
  };
  // Direct coding: 10-pixel template (figure 2), rows top to bottom.
  JB2Decoder.prototype.direct = function (w, h) {
    this.budget(w, h);
    var bm = new Bitmap(w, h);
    var d = bm.data, zp = this.zp, dist = this.bitdist;
    for (var y = h - 1; y >= 0; y--) {
      var up2 = bm.row(y + 2), up1 = bm.row(y + 1), up0 = bm.row(y);
      var ctx = (d[up2 - 1] << 9) | (d[up2] << 8) | (d[up2 + 1] << 7) |
        (d[up1 - 2] << 6) | (d[up1 - 1] << 5) | (d[up1] << 4) | (d[up1 + 1] << 3) | (d[up1 + 2] << 2) |
        (d[up0 - 2] << 1) | d[up0 - 1];
      for (var x = 0; x < w;) {
        var n = zp.decode(dist, ctx);
        d[up0 + x] = n;
        x++;
        ctx = ((ctx << 1) & 0x37a) | (d[up1 + x + 2] << 2) | (d[up2 + x + 1] << 7) | n;
      }
    }
    return bm;
  };
  // Refinement coding: 4 pixels of the new symbol + 7 of the library symbol
  // (figure 3), aligned on the centres of their boxes.
  JB2Decoder.prototype.refine = function (w, h, ref) {
    this.budget(w, h);
    var bm = new Bitmap(w, h);
    var d = bm.data, zp = this.zp, dist = this.cbitdist;
    var cw = ref.w, ch = ref.h;
    var xd2c = (cw - (cw >> 1)) - (w - (w >> 1));
    var yd2c = (ch - (ch >> 1)) - (h - (h >> 1));
    // Reference window in the new symbol's coordinates, one pixel of margin
    // (two on the right), zero outside the reference symbol.
    var ww = w + 3, wh = h + 2;
    var win = new Uint8Array(ww * wh);
    for (var wy = 0; wy < wh; wy++) {
      var ry = wy - 1 + yd2c;
      if (ry < 0 || ry >= ch) continue;
      for (var wx = 0; wx < ww; wx++) {
        var rx = wx - 1 + xd2c;
        if (rx >= 0 && rx < cw) win[wy * ww + wx] = ref.bits[ry * cw + rx];
      }
    }
    for (var y = h - 1; y >= 0; y--) {
      var up1 = bm.row(y + 1), up0 = bm.row(y);
      var r1 = (y + 2) * ww + 1, r0 = (y + 1) * ww + 1, rd = y * ww + 1;
      var ctx = (d[up1 - 1] << 10) | (d[up1] << 9) | (d[up1 + 1] << 8) | (d[up0 - 1] << 7) |
        (win[r1] << 6) | (win[r0 - 1] << 5) | (win[r0] << 4) | (win[r0 + 1] << 3) |
        (win[rd - 1] << 2) | (win[rd] << 1) | win[rd + 1];
      for (var x = 0; x < w;) {
        var n = zp.decode(dist, ctx);
        d[up0 + x] = n;
        x++;
        ctx = ((ctx << 1) & 0x636) | (d[up1 + x + 1] << 8) | (win[r1 + x] << 6) |
          (win[r0 + x + 1] << 3) | win[rd + x + 1] | (n << 7);
      }
    }
    return bm;
  };
  JB2Decoder.prototype.fillShortList = function (v) {
    this.sl0 = this.sl1 = this.sl2 = v;
    this.slPos = 0;
  };
  JB2Decoder.prototype.updateShortList = function (v) {
    if (++this.slPos === 3) this.slPos = 0;
    if (this.slPos === 0) this.sl0 = v; else if (this.slPos === 1) this.sl1 = v; else this.sl2 = v;
    var a = this.sl0, b = this.sl1, c = this.sl2;
    return (a >= b) ? ((a > c) ? ((b >= c) ? b : c) : a) : ((a < c) ? ((b >= c) ? c : b) : a);
  };
  JB2Decoder.prototype.startImage = function (w, h) {
    this.lastLeft = 1 + w;
    this.lastRowLeft = 0;
    this.lastRowBottom = h;
    this.lastRight = 0;
    this.lastBottom = h;
    this.fillShortList(h);
    this.gotStart = true;
  };
  // Location of a symbol relative to its text line (spec 11.5.8).
  JB2Decoder.prototype.location = function (rows, cols) {
    if (!this.gotStart) throw DjVuError('JB2_CORRUPT', 'symbol before start of image');
    var left, top, bottom;
    if (this.zp.decode(this.offsetTypeCtx, 0)) {
      var xd = this.num(BIGNEG, BIGPOS, NC_REL_LOC_X_LAST);
      var yd = this.num(BIGNEG, BIGPOS, NC_REL_LOC_Y_LAST);
      left = this.lastRowLeft + xd;
      top = this.lastRowBottom + yd;
      bottom = top - rows + 1;
      this.lastLeft = this.lastRowLeft = left;
      this.lastRight = left + cols - 1;
      this.lastBottom = this.lastRowBottom = bottom;
      this.fillShortList(bottom);
    } else {
      var xd2 = this.num(BIGNEG, BIGPOS, NC_REL_LOC_X_CUR);
      var yd2 = this.num(BIGNEG, BIGPOS, NC_REL_LOC_Y_CUR);
      left = this.lastRight + xd2;
      bottom = this.lastBottom + yd2;
      this.lastLeft = left;
      this.lastRight = left + cols - 1;
      this.lastBottom = this.updateShortList(bottom);
    }
    return { left: left - 1, bottom: bottom - 1 };
  };
  JB2Decoder.prototype.addLibrary = function (shape) { this.lib.push(shape); };
  JB2Decoder.prototype.addBlit = function (shape, left, bottom) {
    if (shape.w && shape.h) this.blits.push(shape, left, bottom);
  };
  JB2Decoder.prototype.absSize = function () {
    var w = this.num(0, BIGPOS, NC_ABS_SIZE_X);
    var h = this.num(0, BIGPOS, NC_ABS_SIZE_Y);
    return [w, h];
  };
  JB2Decoder.prototype.match = function () {
    if (!this.lib.length) throw DjVuError('JB2_CORRUPT', 'match against an empty library');
    return this.lib[this.num(0, this.lib.length - 1, NC_MATCH_INDEX)];
  };
  JB2Decoder.prototype.relSize = function (ref) {
    var w = ref.w + this.num(BIGNEG, BIGPOS, NC_REL_SIZE_X);
    var h = ref.h + this.num(BIGNEG, BIGPOS, NC_REL_SIZE_Y);
    if (w < 0 || h < 0) throw DjVuError('JB2_CORRUPT', 'negative symbol size');
    return [w, h];
  };
  JB2Decoder.prototype.run = function () {
    var isDict = !!this.opts.isDict;
    for (var records = 0; records < JB2_MAX_RECORDS; records++) {
      var type = this.num(0, 11, NC_REC_TYPE);
      var sz, bm, t, loc, ref;
      switch (type) {
        case 0: { // start of image
          var w = this.num(0, BIGPOS, NC_IMAGE_SIZE);
          var h = this.num(0, BIGPOS, NC_IMAGE_SIZE);
          this.zp.decode(this.refineCtx, 0); // "eventual lossless refinement": unused
          if (isDict) {
            if (w || h) throw DjVuError('JB2_CORRUPT', 'dictionary with image size');
            this.lastLeft = 1; this.lastRowLeft = 0; this.lastRowBottom = 0;
            this.lastRight = 0; this.lastBottom = 0; this.fillShortList(0);
            this.gotStart = true;
          } else {
            if (!w || !h) throw DjVuError('JB2_CORRUPT', 'empty image size');
            this.width = w;
            this.height = h;
            this.startImage(w, h);
          }
          break;
        }
        case 1: // new symbol, image + library
          sz = this.absSize(); bm = this.direct(sz[0], sz[1]);
          loc = this.location(bm.h, bm.w);
          t = trim(bm); this.addLibrary(t.shape); this.addBlit(t.shape, loc.left + t.dx, loc.bottom + t.dy);
          break;
        case 2: // new symbol, library only
          sz = this.absSize(); bm = this.direct(sz[0], sz[1]);
          this.addLibrary(trim(bm).shape);
          break;
        case 3: // new symbol, image only
          sz = this.absSize(); bm = this.direct(sz[0], sz[1]);
          loc = this.location(bm.h, bm.w);
          t = trim(bm); this.addBlit(t.shape, loc.left + t.dx, loc.bottom + t.dy);
          break;
        case 4: // refined symbol, image + library
          ref = this.match(); sz = this.relSize(ref); bm = this.refine(sz[0], sz[1], ref);
          loc = this.location(bm.h, bm.w);
          t = trim(bm); this.addLibrary(t.shape); this.addBlit(t.shape, loc.left + t.dx, loc.bottom + t.dy);
          break;
        case 5: // refined symbol, library only
          ref = this.match(); sz = this.relSize(ref); bm = this.refine(sz[0], sz[1], ref);
          this.addLibrary(trim(bm).shape);
          break;
        case 6: // refined symbol, image only
          ref = this.match(); sz = this.relSize(ref); bm = this.refine(sz[0], sz[1], ref);
          loc = this.location(bm.h, bm.w);
          t = trim(bm); this.addBlit(t.shape, loc.left + t.dx, loc.bottom + t.dy);
          break;
        case 7: // copy of a library symbol
          ref = this.match();
          loc = this.location(ref.h, ref.w);
          this.addBlit(ref, loc.left, loc.bottom);
          break;
        case 8: { // non-symbol data at an absolute location
          if (!this.gotStart) throw DjVuError('JB2_CORRUPT', 'data before start of image');
          sz = this.absSize(); bm = this.direct(sz[0], sz[1]);
          var left = this.num(1, this.width, NC_ABS_LOC_X);
          var top = this.num(1, this.height, NC_ABS_LOC_Y);
          t = trim(bm); this.addBlit(t.shape, left - 1 + t.dx, top - bm.h + t.dy);
          break;
        }
        case 9: // shared dictionary (before start) or number-context reset
          if (!this.gotStart) {
            var count = this.num(0, BIGPOS, NC_INHERITED);
            if (count > 0) { // some encoders announce an empty dictionary
              var dict = this.opts.dict ? this.opts.dict() : null;
              if (!dict || dict.length < count) throw DjVuError('JB2_NO_DICT', 'shared shape dictionary is missing');
              for (var i = 0; i < count; i++) this.lib.push(dict[i]);
            }
          } else {
            this.resetNumcoder();
          }
          break;
        case 10: { // comment
          var len = this.num(0, BIGPOS, NC_COMMENT_LEN);
          for (var k = 0; k < len; k++) this.num(0, 255, NC_COMMENT_BYTE);
          break;
        }
        case 11: // end of data
          if (!this.gotStart) throw DjVuError('JB2_CORRUPT', 'no start of image');
          return;
        default:
          throw DjVuError('JB2_CORRUPT', 'bad record type ' + type);
      }
    }
    throw DjVuError('JB2_CORRUPT', 'too many records');
  };

  function decodeJB2Dict(buf, start, end, parentDict) {
    var dec = new JB2Decoder(buf, start, end, { isDict: true, dict: parentDict });
    dec.run();
    return dec.lib;
  }

  function decodeJB2Image(buf, start, end, dict) {
    var dec = new JB2Decoder(buf, start, end, { isDict: false, dict: dict });
    dec.run();
    return { width: dec.width, height: dec.height, blits: dec.blits };
  }

  // ------------------------------------------------------------------
  // MMR (Smmr): CCITT group 4 (T.6), optionally in stripes.
  // ------------------------------------------------------------------
  var WHITE_CODES = [
    // terminating 0..63
    '00110101', '000111', '0111', '1000', '1011', '1100', '1110', '1111', '10011', '10100',
    '00111', '01000', '001000', '000011', '110100', '110101', '101010', '101011', '0100111', '0001100',
    '0001000', '0010111', '0000011', '0000100', '0101000', '0101011', '0010011', '0100100', '0011000', '00000010',
    '00000011', '00011010', '00011011', '00010010', '00010011', '00010100', '00010101', '00010110', '00010111', '00101000',
    '00101001', '00101010', '00101011', '00101100', '00101101', '00000100', '00000101', '00001010', '00001011', '01010010',
    '01010011', '01010100', '01010101', '00100100', '00100101', '01011000', '01011001', '01011010', '01011011', '01001010',
    '01001011', '00110010', '00110011', '00110100'
  ];
  var WHITE_MAKEUP = [
    '11011', '10010', '010111', '0110111', '00110110', '00110111', '01100100', '01100101', '01101000', '01100111',
    '011001100', '011001101', '011010010', '011010011', '011010100', '011010101', '011010110', '011010111', '011011000', '011011001',
    '011011010', '011011011', '010011000', '010011001', '010011010', '011000', '010011011'
  ];
  var BLACK_CODES = [
    '0000110111', '010', '11', '10', '011', '0011', '0010', '00011', '000101', '000100',
    '0000100', '0000101', '0000111', '00000100', '00000111', '000011000', '0000010111', '0000011000', '0000001000', '00001100111',
    '00001101000', '00001101100', '00000110111', '00000101000', '00000010111', '00000011000', '000011001010', '000011001011', '000011001100', '000011001101',
    '000001101000', '000001101001', '000001101010', '000001101011', '000011010010', '000011010011', '000011010100', '000011010101', '000011010110', '000011010111',
    '000001101100', '000001101101', '000011011010', '000011011011', '000001010100', '000001010101', '000001010110', '000001010111', '000001100100', '000001100101',
    '000001010010', '000001010011', '000000100100', '000000110111', '000000111000', '000000100111', '000000101000', '000001011000', '000001011001', '000000101011',
    '000000101100', '000001011010', '000001100110', '000001100111'
  ];
  var BLACK_MAKEUP = [
    '0000001111', '000011001000', '000011001001', '000001011011', '000000110011', '000000110100', '000000110101', '0000001101100', '0000001101101', '0000001001010',
    '0000001001011', '0000001001100', '0000001001101', '0000001110010', '0000001110011', '0000001110100', '0000001110101', '0000001110110', '0000001110111', '0000001010010',
    '0000001010011', '0000001010100', '0000001010101', '0000001011010', '0000001011011', '0000001100100', '0000001100101'
  ];
  var EXT_MAKEUP = [
    '00000001000', '00000001100', '00000001101', '000000010010', '000000010011', '000000010100', '000000010101',
    '000000010110', '000000010111', '000000011100', '000000011101', '000000011110', '000000011111'
  ];

  // Binary trie: node = [child0, child1] or a leaf value (number).
  function buildRunTrie(term, makeup) {
    var trie = [null, null];
    function add(code, value) {
      var node = trie;
      for (var i = 0; i < code.length; i++) {
        var bit = code.charCodeAt(i) - 48;
        if (i === code.length - 1) { node[bit] = value; return; }
        if (!node[bit]) node[bit] = [null, null];
        node = node[bit];
      }
    }
    var i;
    for (i = 0; i < term.length; i++) add(term[i], i);
    for (i = 0; i < makeup.length; i++) add(makeup[i], 64 * (i + 1));
    for (i = 0; i < EXT_MAKEUP.length; i++) add(EXT_MAKEUP[i], 1792 + 64 * i);
    return trie;
  }
  var WHITE_TRIE = null, BLACK_TRIE = null;

  function BitReader(buf, start, end) {
    this.buf = buf; this.pos = start; this.end = end; this.bit = 0; this.over = 0;
  }
  BitReader.prototype.read = function () {
    if (this.pos >= this.end) { this.over++; return 0; }
    var v = (this.buf[this.pos] >> (7 - this.bit)) & 1;
    if (++this.bit === 8) { this.bit = 0; this.pos++; }
    return v;
  };

  function readRun(br, trie) {
    var total = 0;
    for (var guard = 0; guard < 64; guard++) {
      var node = trie;
      for (var depth = 0; depth < 14; depth++) {
        node = node[br.read()];
        if (node === null || node === undefined) throw DjVuError('MMR_CORRUPT', 'bad run code');
        if (typeof node === 'number') break;
      }
      if (typeof node !== 'number') throw DjVuError('MMR_CORRUPT', 'bad run code');
      total += node;
      if (node < 64) return total;
    }
    throw DjVuError('MMR_CORRUPT', 'run too long');
  }

  function setBits(out, rowOff, x0, x1) {
    for (var x = x0; x < x1; x++) out[rowOff + (x >> 3)] |= 0x80 >> (x & 7);
  }

  // Decodes `rows` G4 lines into packed rows of `out` (1 = black).
  function g4Rows(buf, start, end, w, rows, out, outOff, stride) {
    if (!WHITE_TRIE) { WHITE_TRIE = buildRunTrie(WHITE_CODES, WHITE_MAKEUP); BLACK_TRIE = buildRunTrie(BLACK_CODES, BLACK_MAKEUP); }
    var br = new BitReader(buf, start, end);
    var ref = new Int32Array(w + 8), cur = new Int32Array(w + 8);
    var refLen = 0;
    ref[0] = w; ref[1] = w;
    for (var y = 0; y < rows; y++) {
      var rowOff = outOff + y * stride;
      var a0 = -1, color = 0, curLen = 0, bi = 0;
      while (a0 < w) {
        if (br.over > 8) throw DjVuError('MMR_CORRUPT', 'data ended mid-row');
        // b1: first change on the reference line right of a0 whose colour
        // becomes the opposite of a0's colour; b2 is the next change.
        bi = bi > 1 ? bi - 2 : 0;
        while (bi < refLen && ref[bi] <= a0) bi++;
        if ((bi & 1) !== color) bi++;
        var b1 = bi < refLen ? ref[bi] : w;
        var b2 = bi + 1 < refLen ? ref[bi + 1] : w;
        var mode;
        if (br.read()) mode = 0; // V0
        else if (br.read()) mode = br.read() ? 1 : -1; // VR1 / VL1
        else if (br.read()) mode = 'H';
        else if (br.read()) mode = 'P';
        else if (br.read()) mode = br.read() ? 2 : -2; // VR2 / VL2
        else if (br.read()) mode = br.read() ? 3 : -3; // VR3 / VL3
        else {
          // 0000000...: extension or end-of-facsimile — the rest is white.
          return;
        }
        var start0 = a0 < 0 ? 0 : a0;
        if (mode === 'P') {
          if (color) setBits(out, rowOff, start0, b2);
          a0 = b2;
        } else if (mode === 'H') {
          var r1 = readRun(br, color ? BLACK_TRIE : WHITE_TRIE);
          var r2 = readRun(br, color ? WHITE_TRIE : BLACK_TRIE);
          var a1 = Math.min(w, start0 + r1), a2 = Math.min(w, a1 + r2);
          if (color) setBits(out, rowOff, start0, a1); else setBits(out, rowOff, a1, a2);
          cur[curLen++] = a1;
          cur[curLen++] = a2;
          a0 = a2;
        } else {
          var av = b1 + mode;
          if (av < start0) av = start0;
          if (av > w) av = w;
          if (color) setBits(out, rowOff, start0, av);
          cur[curLen++] = av;
          a0 = av;
          color ^= 1;
        }
      }
      // The coding line becomes the reference line.
      var tmp = ref; ref = cur; cur = tmp;
      refLen = curLen;
      ref[refLen] = w; ref[refLen + 1] = w;
    }
  }

  function decodeMMR(buf, start, end) {
    if (end - start < 8 || buf[start] !== 0x4d || buf[start + 1] !== 0x4d || buf[start + 2] !== 0x52) {
      throw DjVuError('MMR_CORRUPT', 'bad MMR header');
    }
    var flags = buf[start + 3];
    var invert = (flags & 1) !== 0, striped = (flags & 2) !== 0;
    var w = u16(buf, start + 4), h = u16(buf, start + 6);
    var stride = (w + 7) >> 3;
    var out = new Uint8Array(stride * h);
    var p = start + 8;
    if (striped) {
      var rps = u16(buf, p); p += 2;
      if (!rps) throw DjVuError('MMR_CORRUPT', 'zero rows per stripe');
      for (var y = 0; y < h; y += rps) {
        if (p + 4 > end) break;
        var n = u32(buf, p); p += 4;
        var rows = Math.min(rps, h - y);
        g4Rows(buf, p, Math.min(end, p + n), w, rows, out, y * stride, stride);
        p += n;
      }
    } else {
      g4Rows(buf, p, end, w, h, out, 0, stride);
    }
    if (invert) {
      for (var i = 0; i < out.length; i++) out[i] ^= 0xff;
      clearPadding(out, w, h, stride);
    }
    return { width: w, height: h, stride: stride, bits: out };
  }

  function clearPadding(out, w, h, stride) {
    if (!(w & 7)) return;
    var mask = (0xff00 >> (w & 7)) & 0xff;
    for (var y = 0; y < h; y++) out[y * stride + stride - 1] &= mask;
  }

  // ------------------------------------------------------------------
  // IW44 — wavelet images (appendix 1).
  // ------------------------------------------------------------------
  var IW_QUANT = [0x004000, 0x008000, 0x008000, 0x010000, 0x010000, 0x010000, 0x020000, 0x020000,
    0x020000, 0x040000, 0x040000, 0x040000, 0x080000, 0x040000, 0x040000, 0x080000];
  var BAND_START = [0, 1, 2, 3, 4, 8, 12, 16, 32, 48];
  var BAND_SIZE = [1, 1, 1, 1, 4, 4, 4, 16, 16, 16];
  var ZERO = 1, ACTIVE = 2, NEW = 4, UNK = 8;

  // Coefficient index -> position in the 32x32 block (row * 32 + col), rows
  // counted from the bottom: row bits b1 b3 b5 b7 b9, column bits b0 b2 b4
  // b6 b8, most significant first (spec 10.6.3).
  var ZIGZAG = new Uint16Array(1024);
  (function () {
    for (var i = 0; i < 1024; i++) {
      var row = (((i >> 1) & 1) << 4) | (((i >> 3) & 1) << 3) | (((i >> 5) & 1) << 2) | (((i >> 7) & 1) << 1) | ((i >> 9) & 1);
      var col = ((i & 1) << 4) | (((i >> 2) & 1) << 3) | (((i >> 4) & 1) << 2) | (((i >> 6) & 1) << 1) | ((i >> 8) & 1);
      ZIGZAG[i] = row * 32 + col;
    }
  })();

  function IWCodec(w, h) {
    this.w = w;
    this.h = h;
    this.bw = (w + 31) & ~31;
    this.bh = (h + 31) & ~31;
    this.nb = (this.bw >> 5) * (this.bh >> 5);
    this.coeff = new Int16Array(this.nb * 1024);
    this.quantLo = new Int32Array(16);
    this.quantHi = new Int32Array(10);
    var i = 0, q = 0, j;
    for (j = 0; j < 4; j++) this.quantLo[i++] = IW_QUANT[q++];
    for (j = 0; j < 4; j++) this.quantLo[i++] = IW_QUANT[q];
    q++;
    for (j = 0; j < 4; j++) this.quantLo[i++] = IW_QUANT[q];
    q++;
    for (j = 0; j < 4; j++) this.quantLo[i++] = IW_QUANT[q];
    q++;
    this.quantHi[0] = 0;
    for (j = 1; j < 10; j++) this.quantHi[j] = IW_QUANT[q++];
    this.curband = 0;
    this.curbit = 1;
    this.ctxStart = new Uint8Array(16);
    this.ctxBucket = new Uint8Array(80);
    this.ctxMant = new Uint8Array(1);
    this.ctxRoot = new Uint8Array(1);
    this.cstate = new Uint8Array(256);
    this.bstate = new Uint8Array(16);
  }
  IWCodec.prototype.isNullSlice = function (band) {
    if (band === 0) {
      var isNull = true;
      for (var i = 0; i < 16; i++) {
        var t = this.quantLo[i];
        this.cstate[i] = ZERO;
        if (t > 0 && t < 0x8000) { this.cstate[i] = UNK; isNull = false; }
      }
      return isNull;
    }
    var th = this.quantHi[band];
    return !(th > 0 && th < 0x8000);
  };
  IWCodec.prototype.codeSlice = function (zp) {
    if (this.curbit < 0) return 0;
    var band = this.curband;
    if (!this.isNullSlice(band)) {
      var fb = BAND_START[band], nbk = BAND_SIZE[band];
      for (var b = 0; b < this.nb; b++) this.decodeBuckets(zp, band, b * 1024, fb, nbk);
    }
    // finish the slice: halve this band's step sizes, move to the next band
    this.quantHi[band] >>= 1;
    if (band === 0) for (var i = 0; i < 16; i++) this.quantLo[i] >>= 1;
    if (++this.curband >= 10) {
      this.curband = 0;
      this.curbit++;
      if (this.quantHi[9] === 0) { this.curbit = -1; return 0; }
    }
    return 1;
  };
  IWCodec.prototype.decodeBuckets = function (zp, band, blk, fbucket, nbucket) {
    var coeff = this.coeff, cstate = this.cstate, bstate = this.bstate;
    var bbstate = 0, b, i, base, cs, s, bs;
    // Preliminary flags (spec 10.5.1).
    if (fbucket) {
      for (b = 0; b < nbucket; b++) {
        base = blk + (fbucket + b) * 16;
        cs = b * 16;
        bs = 0;
        for (i = 0; i < 16; i++) {
          s = coeff[base + i] ? ACTIVE : UNK;
          cstate[cs + i] = s;
          bs |= s;
        }
        bstate[b] = bs;
        bbstate |= bs;
      }
    } else {
      for (i = 0; i < 16; i++) {
        s = cstate[i];
        if (s !== ZERO) s = coeff[blk + i] ? ACTIVE : UNK;
        cstate[i] = s;
        bbstate |= s;
      }
      bstate[0] = bbstate;
    }
    // Pass 1: decode the whole block band?
    if (nbucket < 16 || (bbstate & ACTIVE)) bbstate |= NEW;
    else if (bbstate & UNK) { if (zp.decode(this.ctxRoot, 0)) bbstate |= NEW; }
    // Pass 2: which buckets carry new coefficients.
    if (bbstate & NEW) {
      for (b = 0; b < nbucket; b++) {
        if (!(bstate[b] & UNK)) continue;
        var n = 0;
        if (band > 0) {
          var k = blk + ((fbucket + b) << 2);
          if (coeff[k]) n++;
          if (coeff[k + 1]) n++;
          if (coeff[k + 2]) n++;
          if (n < 3 && coeff[k + 3]) n++;
        }
        if (bbstate & ACTIVE) n |= 4;
        if (zp.decode(this.ctxBucket, band * 8 + n)) bstate[b] |= NEW;
      }
    }
    // Pass 3: newly active coefficients and their signs.
    if (bbstate & NEW) {
      var thres = this.quantHi[band];
      for (b = 0; b < nbucket; b++) {
        if (!(bstate[b] & NEW)) continue;
        base = blk + (fbucket + b) * 16;
        cs = b * 16;
        var gotcha = 0;
        for (i = 0; i < 16; i++) if (cstate[cs + i] & UNK) gotcha++;
        for (i = 0; i < 16; i++) {
          if (!(cstate[cs + i] & UNK)) continue;
          if (band === 0) thres = this.quantLo[i];
          var ctx = gotcha >= 7 ? 7 : gotcha;
          if (bstate[b] & ACTIVE) ctx |= 8;
          if (zp.decode(this.ctxStart, ctx)) {
            cstate[cs + i] |= NEW;
            var half = thres >> 1;
            var v = thres + half - (half >> 2); // spec differs: 11/8 step, not 3/2
            coeff[base + i] = zp.decodeIW() ? -v : v;
          }
          if (cstate[cs + i] & NEW) gotcha = 0;
          else if (gotcha > 0) gotcha--;
        }
      }
    }
    // Pass 4: one more bit of the previously active coefficients.
    if (bbstate & ACTIVE) {
      var th2 = this.quantHi[band];
      for (b = 0; b < nbucket; b++) {
        if (!(bstate[b] & ACTIVE)) continue;
        base = blk + (fbucket + b) * 16;
        cs = b * 16;
        for (i = 0; i < 16; i++) {
          if (!(cstate[cs + i] & ACTIVE)) continue;
          var c = coeff[base + i];
          var a = c < 0 ? -c : c;
          if (band === 0) th2 = this.quantLo[i];
          if (a <= 3 * th2) {
            a += th2 >> 2; // spec differs: quarter-step bias on the second bit
            if (zp.decode(this.ctxMant, 0)) a += th2 >> 1;
            else a += -th2 + (th2 >> 1);
          } else {
            if (zp.decodeIW()) a += th2 >> 1;
            else a += -th2 + (th2 >> 1);
          }
          coeff[base + i] = c > 0 ? a : -a;
        }
      }
    }
  };

  // Inverse (4,4) Deslauriers-Dubuc lifting along one line of samples
  // p[off + k*step], k = 0..n-1, in place (Int16 storage wraps like the
  // reference). All updates first, then all predictions.
  function unlift(p, off, step, n) {
    var k, a, b;
    for (k = 0; k < n; k += 2) {
      var m1 = k - 1 >= 0 ? p[off + (k - 1) * step] : 0;
      var p1 = k + 1 < n ? p[off + (k + 1) * step] : 0;
      var m3 = k - 3 >= 0 ? p[off + (k - 3) * step] : 0;
      var p3 = k + 3 < n ? p[off + (k + 3) * step] : 0;
      a = m1 + p1;
      b = m3 + p3;
      p[off + k * step] -= ((a << 3) + a - b + 16) >> 5;
    }
    for (k = 1; k < n; k += 2) {
      if (k - 3 >= 0 && k + 3 < n) {
        a = p[off + (k - 1) * step] + p[off + (k + 1) * step];
        b = p[off + (k - 3) * step] + p[off + (k + 3) * step];
        p[off + k * step] += ((a << 3) + a - b + 8) >> 4;
      } else if (k + 1 < n) {
        a = p[off + (k - 1) * step] + p[off + (k + 1) * step];
        p[off + k * step] += (a + 1) >> 1;
      } else {
        p[off + k * step] += p[off + (k - 1) * step];
      }
    }
  }

  // The same inverse step along a ROW, as real encoders expect it: spec
  // differs. The reference decoder runs this direction as a register
  // pipeline, and two of its edge cases do not match the text of the spec:
  // near the start of a short row (4..7 samples at a coarse scale) a missing
  // right neighbour keeps the value the register already held instead of 0,
  // and lifted samples are reused before 16-bit truncation. Narrow layers
  // (foregrounds, thumbnails) are off by up to 2 levels without this.
  function unliftRow(p, off, step, n) {
    var a0 = 0, a1 = 0, a2 = 0, a3 = 0, b0 = 0, b1 = 0, b2 = 0, b3 = 0;
    var x = 0, i, j;
    if (x < n) {
      if (x + 1 < n) a2 = p[off + step];
      if (x + 3 < n) a3 = p[off + 3 * step];
      b3 = p[off] - ((((a1 + a2) << 3) + (a1 + a2) - a0 - a3 + 16) >> 5);
      b2 = b3;
      p[off] = b3;
      x += 2;
    }
    if (x < n) {
      a0 = a1; a1 = a2; a2 = a3;
      if (x + 3 < n) a3 = p[off + (x + 3) * step];
      i = off + x * step;
      b3 = p[i] - ((((a1 + a2) << 3) + (a1 + a2) - a0 - a3 + 16) >> 5);
      p[i] = b3;
      x += 2;
    }
    if (x < n) {
      b1 = b2; b2 = b3;
      a0 = a1; a1 = a2; a2 = a3;
      if (x + 3 < n) a3 = p[off + (x + 3) * step];
      i = off + x * step;
      b3 = p[i] - ((((a1 + a2) << 3) + (a1 + a2) - a0 - a3 + 16) >> 5);
      p[i] = b3;
      j = off + (x - 3) * step;
      p[j] = p[j] + ((b1 + b2 + 1) >> 1);
      x += 2;
    }
    while (x + 3 < n) {
      a0 = a1; a1 = a2; a2 = a3; a3 = p[off + (x + 3) * step];
      b0 = b1; b1 = b2; b2 = b3;
      i = off + x * step;
      b3 = p[i] - ((((a1 + a2) << 3) + (a1 + a2) - a0 - a3 + 16) >> 5);
      p[i] = b3;
      j = off + (x - 3) * step;
      p[j] = p[j] + ((((b1 + b2) << 3) + (b1 + b2) - b0 - b3 + 8) >> 4);
      x += 2;
    }
    while (x < n) {
      a0 = a1; a1 = a2; a2 = a3; a3 = 0;
      b0 = b1; b1 = b2; b2 = b3;
      i = off + x * step;
      b3 = p[i] - ((((a1 + a2) << 3) + (a1 + a2) - a0 - a3 + 16) >> 5);
      p[i] = b3;
      j = off + (x - 3) * step;
      p[j] = p[j] + ((((b1 + b2) << 3) + (b1 + b2) - b0 - b3 + 8) >> 4);
      x += 2;
    }
    while (x - 3 < n) {
      b0 = b1; b1 = b2; b2 = b3;
      if (x - 3 >= 0) {
        j = off + (x - 3) * step;
        p[j] = p[j] + ((b1 + b2 + 1) >> 1);
      }
      x += 2;
    }
  }

  // Coefficients -> pixel plane (bw x bh, rows bottom-up), values with six
  // fractional bits. `endScale` 2 reconstructs a half-resolution plane
  // (subsampled chroma) and replicates it 2x2.
  IWCodec.prototype.reconstruct = function (endScale) {
    var bw = this.bw, bh = this.bh, w = this.w, h = this.h;
    var data = new Int16Array(bw * bh);
    var coeff = this.coeff;
    var blk = 0;
    for (var by = 0; by < bh; by += 32) {
      for (var bx = 0; bx < bw; bx += 32, blk++) {
        var base = blk * 1024;
        for (var i = 0; i < 1024; i++) {
          var v = coeff[base + i];
          if (!v) continue;
          var loc = ZIGZAG[i];
          data[(by + (loc >> 5)) * bw + bx + (loc & 31)] = v;
        }
      }
    }
    for (var s = 16; s >= endScale; s >>= 1) {
      var ny = Math.floor((h - 1) / s) + 1;
      var nx = Math.floor((w - 1) / s) + 1;
      for (var x = 0; x < w; x += s) unlift(data, x, s * bw, ny);        // vertical
      for (var y = 0; y < h; y += s) unliftRow(data, y * bw, s, nx);     // horizontal
    }
    if (endScale === 2) {
      for (var yy = 0; yy < bh; yy += 2) {
        for (var xx = 0; xx < bw; xx += 2) {
          var q = yy * bw + xx, val = data[q];
          data[q + 1] = val;
          data[q + bw] = val;
          data[q + bw + 1] = val;
        }
      }
    }
    return data;
  };

  // The same transform stopped at scale `sub` (2, 4, 8, 16) on a plane that
  // only holds the samples that scale keeps: (bw/sub) x (bh/sub), rows
  // bottom-up. Finer coefficients are discarded, exactly as a subsampled
  // DjVu render does — a quarter or a sixteenth of the memory, no resampling.
  IWCodec.prototype.reconstructReduced = function (sub) {
    if (sub <= 1) return { data: this.reconstruct(1), stride: this.bw };
    var cw = this.bw / sub, chh = this.bh / sub, w = this.w, h = this.h;
    var data = new Int16Array(cw * chh);
    var coeff = this.coeff;
    var m = sub - 1;
    var blk = 0;
    for (var by = 0; by < this.bh; by += 32) {
      for (var bx = 0; bx < this.bw; bx += 32, blk++) {
        var base = blk * 1024;
        for (var i = 0; i < 1024; i++) {
          var v = coeff[base + i];
          if (!v) continue;
          var loc = ZIGZAG[i], r = loc >> 5, c = loc & 31;
          if ((r & m) || (c & m)) continue;
          data[((by + r) / sub) * cw + (bx + c) / sub] = v;
        }
      }
    }
    for (var s = 16; s >= sub; s >>= 1) {
      var sc = s / sub;
      var ny = Math.floor((h - 1) / s) + 1;
      var nx = Math.floor((w - 1) / s) + 1;
      for (var xi = 0; xi < nx; xi++) unlift(data, xi * sc, sc * cw, ny);
      for (var yi = 0; yi < ny; yi++) unliftRow(data, yi * sc * cw, sc, nx);
    }
    return { data: data, stride: cw };
  };

  function IW44Layer() {
    this.serial = 0;
    this.cslice = 0;
    this.y = null;
    this.cb = null;
    this.cr = null;
    this.width = 0;
    this.height = 0;
    this.crcbDelay = 0;
    this.crcbHalf = false;
  }
  IW44Layer.prototype.decodeChunk = function (buf, start, end) {
    if (end - start < 2) throw DjVuError('IW44_CORRUPT', 'empty wavelet chunk');
    var p = start;
    var serial = buf[p++], slices = buf[p++];
    if (serial !== this.serial) throw DjVuError('IW44_ORDER', 'wavelet chunks out of order');
    if (serial === 0) {
      if (end - start < 9 - 1) throw DjVuError('IW44_CORRUPT', 'short wavelet header');
      var major = buf[p++], minor = buf[p++];
      if ((major & 0x7f) !== 1) throw DjVuError('IW44_VERSION', 'unknown wavelet codec ' + (major & 0x7f));
      var w = u16(buf, p), h = u16(buf, p + 2);
      p += 4;
      var delay = 0, half = false;
      if (minor >= 2) {
        var cb = buf[p++];
        delay = cb & 0x7f;
        half = (cb & 0x80) === 0; // spec differs: a clear bit means half-resolution chroma
      }
      if (major & 0x80) delay = -1; // grayscale
      if (!w || !h) throw DjVuError('IW44_CORRUPT', 'empty wavelet image');
      if (w * h > 400 * 1000 * 1000) throw DjVuError('IW44_CORRUPT', 'wavelet image too large');
      this.width = w;
      this.height = h;
      this.crcbDelay = delay;
      this.crcbHalf = half;
      this.y = new IWCodec(w, h);
      if (delay >= 0) { this.cb = new IWCodec(w, h); this.cr = new IWCodec(w, h); }
    } else if (!this.y) {
      throw DjVuError('IW44_ORDER', 'wavelet refinement without header');
    }
    var zp = new ZPDecoder(buf, p, end);
    var target = this.cslice + slices;
    var flag = 1;
    while (flag && this.cslice < target) {
      flag = this.y.codeSlice(zp);
      if (this.cb && this.crcbDelay <= this.cslice) {
        flag |= this.cb.codeSlice(zp);
        flag |= this.cr.codeSlice(zp);
      }
      this.cslice++;
    }
    this.serial++;
  };
  // Pixels, top-down rows: {width, height, channels: 1|3, data, sub}.
  // `sub` (1, 2, 4, 8, 16) renders at 1/sub of the layer's resolution:
  // ceil(width/sub) x ceil(height/sub), anchored bottom-left like the layer.
  IW44Layer.prototype.image = function (sub) {
    if (!this.y) return null;
    sub = sub === 2 || sub === 4 || sub === 8 || sub === 16 ? sub : 1;
    var w = Math.ceil(this.width / sub), h = Math.ceil(this.height / sub);
    var yr = this.y.reconstructReduced(sub);
    var Y = yr.data, bw = yr.stride;
    var i, x, y;
    if (!this.cb) {
      var gray = new Uint8Array(w * h);
      for (y = 0; y < h; y++) {
        var src = (h - 1 - y) * bw, dst = y * w;
        for (x = 0; x < w; x++) {
          var v = (Y[src + x] + 32) >> 6;
          if (v < -128) v = -128; else if (v > 127) v = 127;
          gray[dst + x] = 127 - v;
        }
      }
      return { width: w, height: h, channels: 1, data: gray, sub: sub };
    }
    var Cb, Cr;
    if (sub === 1) {
      var endScale = this.crcbHalf ? 2 : 1;
      Cb = this.cb.reconstruct(endScale);
      Cr = this.cr.reconstruct(endScale);
    } else {
      // half-resolution chroma carries nothing finer than scale 2, so any
      // reduced render reads it directly at `sub`
      Cb = this.cb.reconstructReduced(sub).data;
      Cr = this.cr.reconstructReduced(sub).data;
    }
    var rgb = new Uint8Array(w * h * 3);
    for (y = 0; y < h; y++) {
      var s = (h - 1 - y) * bw, d = y * w * 3;
      for (x = 0; x < w; x++) {
        var yv = (Y[s + x] + 32) >> 6;
        var bv = (Cb[s + x] + 32) >> 6;
        var rv = (Cr[s + x] + 32) >> 6;
        if (yv < -128) yv = -128; else if (yv > 127) yv = 127;
        if (bv < -128) bv = -128; else if (bv > 127) bv = 127;
        if (rv < -128) rv = -128; else if (rv > 127) rv = 127;
        var t1 = bv >> 2;
        var t2 = rv + (rv >> 1);
        var t3 = yv + 128 - t1;
        var tr = yv + 128 + t2;
        var tg = t3 - (t2 >> 1);
        var tb = t3 + (bv << 1);
        i = d + x * 3;
        rgb[i] = tr < 0 ? 0 : (tr > 255 ? 255 : tr);
        rgb[i + 1] = tg < 0 ? 0 : (tg > 255 ? 255 : tg);
        rgb[i + 2] = tb < 0 ? 0 : (tb > 255 ? 255 : tb);
      }
    }
    return { width: w, height: h, channels: 3, data: rgb, sub: sub };
  };

  // ------------------------------------------------------------------
  // FGbz — one colour per blit.
  // ------------------------------------------------------------------
  function decodeFGbz(buf, start, end) {
    var ver = buf[start];
    var n = u16(buf, start + 1);
    var colors = new Uint8Array(n * 3);
    var p = start + 3;
    for (var i = 0; i < n; i++, p += 3) {
      colors[i * 3] = buf[p + 2];     // stored B, G, R
      colors[i * 3 + 1] = buf[p + 1];
      colors[i * 3 + 2] = buf[p];
    }
    var indices = null;
    if ((ver & 0x80) && p + 3 <= end) {
      var count = u24(buf, p); p += 3;
      var z = bzzDecode(buf, p, end);
      indices = new Uint16Array(count);
      for (i = 0; i < count && 2 * i + 1 < z.length; i++) indices[i] = u16(z, 2 * i);
    }
    return { colors: colors, indices: indices };
  }

  // ------------------------------------------------------------------
  // Hidden text (TXTa / TXTz).
  // ------------------------------------------------------------------
  var ZONE_PAGE = 1, ZONE_COLUMN = 2, ZONE_REGION = 3, ZONE_PARAGRAPH = 4, ZONE_LINE = 5, ZONE_WORD = 6, ZONE_CHAR = 7;

  function decodeText(data) {
    if (data.length < 3) return null;
    var len = u24(data, 0);
    var textBytes = data.subarray(3, Math.min(data.length, 3 + len));
    var st = { d: data, p: 3 + len };
    var root = null;
    if (st.p + 1 + 17 <= data.length) {
      st.p++; // version
      try { root = decodeZone(st, textBytes.length, null, null, 0); }
      catch (e) { root = null; }
    }
    return { textBytes: textBytes, text: utf8(textBytes), root: root };
  }

  function decodeZone(st, maxText, parent, prev, depth) {
    var d = st.d, p = st.p;
    if (p + 17 > d.length || depth > 16) throw DjVuError('TXT_CORRUPT', 'text layer truncated');
    var type = d[p];
    var x = u16(d, p + 1) - 0x8000;
    var y = u16(d, p + 3) - 0x8000;
    var w = u16(d, p + 5) - 0x8000;
    var h = u16(d, p + 7) - 0x8000;
    var ts = u16(d, p + 9) - 0x8000; // spec differs: a relative text offset, not "0"
    var tl = u24(d, p + 11);
    var nChildren = u24(d, p + 14);
    st.p = p + 17;
    if (prev) {
      if (type === ZONE_PAGE || type === ZONE_PARAGRAPH || type === ZONE_LINE) {
        x = x + prev.xmin;
        y = prev.ymin - (y + h);
      } else {
        x = x + prev.xmax;
        y = y + prev.ymin;
      }
      ts += prev.ts + prev.tl;
    } else if (parent) {
      x = x + parent.xmin;
      y = parent.ymax - (y + h);
      ts += parent.ts;
    }
    var zone = { type: type, xmin: x, ymin: y, xmax: x + w, ymax: y + h, ts: ts, tl: tl, children: [] };
    if (zone.ts < 0 || zone.ts + zone.tl > maxText) { zone.ts = 0; zone.tl = 0; }
    var prevChild = null;
    for (var i = 0; i < nChildren; i++) {
      var c = decodeZone(st, maxText, zone, prevChild, depth + 1);
      zone.children.push(c);
      prevChild = c;
    }
    return zone;
  }

  // Words with boxes (DjVu coordinates, origin bottom-left), in reading
  // order; lines without word zones become one run. FineReader writes tabs
  // and other separators inside zones: collapsed to single spaces.
  function textRuns(text) {
    var out = [];
    if (!text || !text.root) return out;
    var bytes = text.textBytes;
    var lineNo = 0;
    function clean(s) { return s.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim(); }
    function zoneText(z) { return clean(utf8(bytes.subarray(z.ts, z.ts + z.tl))); }
    function visit(z) {
      if (z.type === ZONE_LINE) {
        lineNo++;
        var words = [];
        collect(z, ZONE_WORD, words);
        if (words.length) {
          for (var i = 0; i < words.length; i++) push(words[i], lineNo);
        } else {
          push(z, lineNo);
        }
        return;
      }
      if (z.type === ZONE_WORD) { lineNo++; push(z, lineNo); return; }
      if (!z.children.length && z.type !== ZONE_CHAR) { lineNo++; push(z, lineNo); return; }
      for (var j = 0; j < z.children.length; j++) visit(z.children[j]);
    }
    function collect(z, type, acc) {
      for (var i = 0; i < z.children.length; i++) {
        var c = z.children[i];
        if (c.type === type) acc.push(c); else collect(c, type, acc);
      }
    }
    function push(z, line) {
      if (z.xmax <= z.xmin || z.ymax <= z.ymin) return;
      var s = zoneText(z);
      if (!s) return;
      out.push({ text: s, xmin: z.xmin, ymin: z.ymin, xmax: z.xmax, ymax: z.ymax, line: line });
    }
    visit(text.root);
    return out;
  }

  // ------------------------------------------------------------------
  // Annotations (ANTa / ANTz): a small S-expression language.
  // ------------------------------------------------------------------
  function parseSexp(s) {
    var i = 0, n = s.length;
    function ws() { while (i < n && /\s/.test(s.charAt(i))) i++; }
    // Octal escapes are UTF-8 BYTES (djvused writes Cyrillic as \320\272...):
    // collect bytes and decode once, or the text comes out as mojibake.
    function str() {
      i++; // opening quote
      var bytes = [];
      function pushText(t) {
        var enc = unescape(encodeURIComponent(t));
        for (var k = 0; k < enc.length; k++) bytes.push(enc.charCodeAt(k));
      }
      while (i < n) {
        var c = s.charAt(i++);
        if (c === '"') break;
        if (c === '\\' && i < n) {
          var e = s.charAt(i++);
          if (e === 'n') bytes.push(10);
          else if (e === 't') bytes.push(9);
          else if (e === 'r') bytes.push(13);
          else if (e === 'b') bytes.push(8);
          else if (e === 'f') bytes.push(12);
          else if (e === 'v') bytes.push(11);
          else if (e === 'a') bytes.push(7);
          else if (e >= '0' && e <= '7') {
            var oct = e;
            while (oct.length < 3 && i < n && s.charAt(i) >= '0' && s.charAt(i) <= '7') oct += s.charAt(i++);
            bytes.push(parseInt(oct, 8) & 0xff);
          } else pushText(e);
        } else pushText(c);
      }
      return utf8(new Uint8Array(bytes));
    }
    function atom() {
      var st = i;
      while (i < n && !/[\s()"]/.test(s.charAt(i))) i++;
      return s.slice(st, i);
    }
    function list(depth) {
      var out = [];
      while (i < n) {
        ws();
        if (i >= n) break;
        var c = s.charAt(i);
        if (c === ')') { i++; return out; }
        if (c === '(') { i++; if (depth < 64) out.push(list(depth + 1)); else list(depth + 1); }
        else if (c === '"') out.push({ s: str() });
        else out.push(atom());
      }
      return out;
    }
    var top = [];
    while (i < n) {
      ws();
      if (i >= n) break;
      if (s.charAt(i) === '(') { i++; top.push(list(0)); }
      else i++; // stray token
    }
    return top;
  }

  function sexpText(v) { return (v && typeof v === 'object' && !Array.isArray(v)) ? v.s : (typeof v === 'string' ? v : ''); }

  function collectPairs(list, acc, depth) {
    if (depth > 8) return;
    for (var i = 0; i < list.length; i++) {
      var kv = list[i];
      if (!Array.isArray(kv)) continue;
      if (kv.length >= 2 && typeof kv[0] === 'string' && kv[1] && typeof kv[1] === 'object' && !Array.isArray(kv[1])) acc[kv[0]] = sexpText(kv[1]);
      else collectPairs(kv, acc, depth + 1);
    }
  }

  function decodeAnnotations(text, pageW, pageH) {
    var out = { links: [], metadata: {}, background: null, zoom: null, mode: null, xmp: null };
    var items;
    try { items = parseSexp(text); } catch (e) { return out; }
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!Array.isArray(it) || !it.length) continue;
      var head = it[0];
      if (head === 'background' && it[1]) out.background = String(it[1]);
      else if (head === 'zoom' && it[1]) out.zoom = String(it[1]);
      else if (head === 'mode' && it[1]) out.mode = String(it[1]);
      else if (head === 'xmp' && it[1]) out.xmp = sexpText(it[1]);
      else if (head === 'metadata') {
        // Lenient: some writers wrap the pairs in extra parentheses.
        collectPairs(it.slice(1), out.metadata, 0);
      } else if (head === 'maparea') {
        var link = mapArea(it);
        if (link) out.links.push(link);
      }
    }
    return out;
  }

  function mapArea(it) {
    var url = '', target = '';
    var u = it[1];
    if (Array.isArray(u)) {
      if (u[0] === 'url') { url = sexpText(u[1]); target = sexpText(u[2]); }
    } else url = sexpText(u);
    var comment = sexpText(it[2]);
    var shape = null;
    for (var i = 3; i < it.length; i++) {
      var s = it[i];
      if (Array.isArray(s) && (s[0] === 'rect' || s[0] === 'oval' || s[0] === 'text' || s[0] === 'poly' || s[0] === 'line')) { shape = s; break; }
    }
    if (!shape || !url) return null;
    var nums = [];
    for (var k = 1; k < shape.length; k++) {
      var v = parseInt(shape[k], 10);
      if (!isFinite(v)) return null;
      nums.push(v);
    }
    var box;
    if (shape[0] === 'poly' || shape[0] === 'line') {
      var xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
      for (k = 0; k + 1 < nums.length; k += 2) {
        xmin = Math.min(xmin, nums[k]); xmax = Math.max(xmax, nums[k]);
        ymin = Math.min(ymin, nums[k + 1]); ymax = Math.max(ymax, nums[k + 1]);
      }
      if (!isFinite(xmin)) return null;
      box = { xmin: xmin, ymin: ymin, xmax: xmax, ymax: ymax };
    } else {
      if (nums.length < 4) return null;
      box = { xmin: nums[0], ymin: nums[1], xmax: nums[0] + nums[2], ymax: nums[1] + nums[3] };
    }
    return { url: url, target: target, comment: comment, shape: shape[0], box: box };
  }

  // ------------------------------------------------------------------
  // Pages. A page is one FORM:DJVU component; shared data (Djbz shape
  // dictionaries, shared annotations) comes from FORM:DJVI components named
  // by INCL chunks, fetched through opts.component(id) -> Uint8Array|null.
  // ------------------------------------------------------------------
  function Component(bytes) {
    this.bytes = bytes;
    var f = openForm(bytes);
    this.form = f.form;
    this.chunks = f.chunks;
    this.truncated = f.truncated;
  }
  Component.prototype.find = function (id) {
    for (var i = 0; i < this.chunks.length; i++) if (this.chunks[i].id === id) return this.chunks[i];
    return null;
  };
  Component.prototype.all = function (id) {
    var out = [];
    for (var i = 0; i < this.chunks.length; i++) if (this.chunks[i].id === id) out.push(this.chunks[i]);
    return out;
  };
  Component.prototype.includes = function () {
    var out = [];
    var inc = this.all('INCL');
    for (var i = 0; i < inc.length; i++) {
      var s = utf8(this.bytes.subarray(inc[i].start, inc[i].end)).replace(/[\0\s]+$/g, '').replace(/^[\0\s]+/g, '');
      if (s) out.push(s);
    }
    return out;
  };

  // opts: { component(id) -> Uint8Array|null, cache: {dicts:{}, comps:{}} }
  function Page(bytes, opts) {
    this.opts = opts || {};
    this.cache = this.opts.cache || { dicts: {}, comps: {} };
    this.comp = new Component(bytes);
    if (this.comp.form !== 'DJVU') throw DjVuError('NOT_A_PAGE', 'component is ' + this.comp.form + ', not a page');
    var info = this.comp.find('INFO');
    if (!info) throw DjVuError('NO_INFO', 'page without INFO');
    this.info = decodeInfo(this.comp.bytes, info.start, info.end);
    this.truncated = this.comp.truncated;
  }
  Page.prototype.includedComponent = function (id) {
    if (Object.prototype.hasOwnProperty.call(this.cache.comps, id)) return this.cache.comps[id];
    var bytes = this.opts.component ? this.opts.component(id) : null;
    var c = null;
    if (bytes) { try { c = new Component(bytes); } catch (e) { c = null; } }
    this.cache.comps[id] = c;
    return c;
  };
  // Every included id the page needs, transitively (the caller prefetches).
  Page.prototype.includeIds = function () {
    var seen = {}, order = [];
    var self = this;
    function walk(comp, depth) {
      if (!comp || depth > 8) return;
      var ids = comp.includes();
      for (var i = 0; i < ids.length; i++) {
        if (seen[ids[i]]) continue;
        seen[ids[i]] = 1;
        order.push(ids[i]);
        walk(self.opts.component ? self.includedComponent(ids[i]) : null, depth + 1);
      }
    }
    walk(this.comp, 0);
    return order;
  };
  Page.prototype.has = function (id) { return !!this.comp.find(id); };
  // Shape dictionary for this page: its own Djbz, else the first Djbz found
  // in included components (depth-first). Decoded dictionaries are cached
  // per component id — a book shares one dictionary across many pages.
  Page.prototype.dictionary = function () {
    var own = this.comp.find('Djbz');
    if (own) return decodeJB2Dict(this.comp.bytes, own.start, own.end, this.dictFromIncludes(this.comp, 0));
    return this.dictFromIncludes(this.comp, 0)();
  };
  Page.prototype.dictFromIncludes = function (comp, depth) {
    var self = this;
    return function () {
      if (depth > 8) return null;
      var ids = comp.includes();
      for (var i = 0; i < ids.length; i++) {
        var id = ids[i];
        if (Object.prototype.hasOwnProperty.call(self.cache.dicts, id)) {
          if (self.cache.dicts[id]) return self.cache.dicts[id];
          continue;
        }
        var c = self.includedComponent(id);
        var d = null;
        if (c) {
          var dj = c.find('Djbz');
          if (dj) d = decodeJB2Dict(c.bytes, dj.start, dj.end, self.dictFromIncludes(c, depth + 1));
          else d = self.dictFromIncludes(c, depth + 1)();
        }
        self.cache.dicts[id] = d;
        if (d) return d;
      }
      return null;
    };
  };
  // Mask: {width, height, stride, bits (packed, top-down, 1 = black),
  //        blits (JB2 only: [shape, left, bottom, ...] bottom-up)}
  Page.prototype.mask = function () {
    var ch = this.comp.find('Sjbz');
    var W = this.info.width, H = this.info.height;
    if (ch) {
      var self = this;
      var img = decodeJB2Image(this.comp.bytes, ch.start, ch.end, function () { return self.dictionary(); });
      return { kind: 'jb2', width: img.width, height: img.height, blits: img.blits, bits: null, stride: (img.width + 7) >> 3 };
    }
    ch = this.comp.find('Smmr');
    if (ch) {
      var m = decodeMMR(this.comp.bytes, ch.start, ch.end);
      return { kind: 'mmr', width: m.width, height: m.height, blits: null, bits: m.bits, stride: m.stride };
    }
    return null;
  };
  // Background: {kind:'iw44', width, height, channels, data, sub, full: {width,
  // height}} or {kind:'jpeg', bytes}. `sub` renders the wavelet layer at
  // 1/sub of its own resolution.
  Page.prototype.background = function (sub) {
    var chunks = this.comp.all('BG44');
    if (chunks.length) {
      var layer = new IW44Layer();
      var err = null;
      for (var i = 0; i < chunks.length; i++) {
        try { layer.decodeChunk(this.comp.bytes, chunks[i].start, chunks[i].end); }
        catch (e) { if (!layer.y) throw e; err = e; break; } // keep what decoded
      }
      var img = layer.image(sub);
      if (img) { img.kind = 'iw44'; img.partial = !!err; img.full = { width: layer.width, height: layer.height }; }
      return img;
    }
    var jp = this.comp.find('BGjp');
    if (jp) return { kind: 'jpeg', bytes: this.comp.bytes.subarray(jp.start, jp.end) };
    return null;
  };
  // Size of the wavelet background without decoding it (null: none / JPEG).
  Page.prototype.backgroundSize = function () {
    var ch = this.comp.find('BG44');
    if (!ch || ch.end - ch.start < 9 || this.comp.bytes[ch.start] !== 0) return null;
    var b = this.comp.bytes, p = ch.start + 4;
    return { width: u16(b, p), height: u16(b, p + 2), gray: (b[ch.start + 2] & 0x80) !== 0 };
  };
  Page.prototype.foreground = function () {
    var fg = this.comp.find('FG44');
    if (fg) {
      var layer = new IW44Layer();
      layer.decodeChunk(this.comp.bytes, fg.start, fg.end);
      var img = layer.image(1);
      if (img) { img.kind = 'iw44'; img.full = { width: layer.width, height: layer.height }; }
      return img;
    }
    var jp = this.comp.find('FGjp');
    if (jp) return { kind: 'jpeg', bytes: this.comp.bytes.subarray(jp.start, jp.end) };
    var pal = this.comp.find('FGbz');
    if (pal) {
      var p = decodeFGbz(this.comp.bytes, pal.start, pal.end);
      p.kind = 'palette';
      return p;
    }
    return null;
  };
  Page.prototype.text = function () {
    var z = this.comp.find('TXTz');
    var data = null;
    if (z) data = bzzDecode(this.comp.bytes, z.start, z.end);
    else {
      var a = this.comp.find('TXTa');
      if (a) data = this.comp.bytes.subarray(a.start, a.end);
    }
    return data ? decodeText(data) : null;
  };
  // Page annotations first, then shared ones from included components.
  Page.prototype.annotations = function () {
    var parts = [];
    var self = this;
    function gather(comp, depth) {
      if (!comp || depth > 8) return;
      for (var i = 0; i < comp.chunks.length; i++) {
        var c = comp.chunks[i];
        if (c.id === 'ANTz') { try { parts.push(utf8(bzzDecode(comp.bytes, c.start, c.end))); } catch (e) { /* skip */ } }
        else if (c.id === 'ANTa') parts.push(utf8(comp.bytes.subarray(c.start, c.end)));
      }
      var ids = comp.includes();
      for (var j = 0; j < ids.length; j++) gather(self.includedComponent(ids[j]), depth + 1);
    }
    gather(this.comp, 0);
    return decodeAnnotations(parts.join('\n'), this.info.width, this.info.height);
  };
  // Summary for the pre-flight card, without decoding any layer.
  Page.prototype.describe = function () {
    var hasMask = this.has('Sjbz') || this.has('Smmr');
    var hasBg = this.has('BG44') || this.has('BGjp');
    var hasFgColor = this.has('FG44') || this.has('FGjp') || this.has('FGbz');
    return {
      width: this.info.width, height: this.info.height, dpi: this.info.dpi, rotation: this.info.rotation,
      kind: hasMask ? (hasBg || hasFgColor ? 'compound' : 'bilevel') : (hasBg ? 'photo' : 'empty'),
      hasText: this.has('TXTz') || this.has('TXTa'),
      truncated: this.truncated
    };
  };

  // Paint a JB2 blit list into packed top-down rows (1 = black). With
  // `colorOf(blitIndex) === key` only the blits of one colour are painted.
  function paintBlits(mask, filter) {
    var W = mask.width, H = mask.height, stride = (W + 7) >> 3;
    var out = new Uint8Array(stride * H);
    var bl = mask.blits;
    for (var i = 0, bi = 0; i < bl.length; i += 3, bi++) {
      if (filter && !filter(bi)) continue;
      var sh = bl[i], x0 = bl[i + 1], y0 = bl[i + 2];
      var sw = sh.w, shh = sh.h, bits = sh.bits;
      for (var sy = 0; sy < shh; sy++) {
        var py = y0 + sy;                // bottom-up page row
        if (py < 0 || py >= H) continue;
        var rowOff = (H - 1 - py) * stride;
        var srow = sy * sw;
        for (var sx = 0; sx < sw; sx++) {
          if (!bits[srow + sx]) continue;
          var px = x0 + sx;
          if (px < 0 || px >= W) continue;
          out[rowOff + (px >> 3)] |= 0x80 >> (px & 7);
        }
      }
    }
    return out;
  }

  function maskBits(mask) {
    if (!mask) return null;
    if (mask.bits) return mask.bits;
    mask.bits = paintBlits(mask, null);
    return mask.bits;
  }

  return {
    VERSION: VERSION,
    DjVuError: DjVuError,
    readDirectory: readDirectory,
    resolveUrl: resolveUrl,
    Page: Page,
    Component: Component,
    maskBits: maskBits,
    paintBlits: paintBlits,
    textRuns: textRuns,
    // exposed for tests
    _internal: {
      ZPDecoder: ZPDecoder, bzzDecode: bzzDecode, decodeJB2Image: decodeJB2Image, decodeJB2Dict: decodeJB2Dict,
      IW44Layer: IW44Layer, decodeMMR: decodeMMR, decodeText: decodeText, decodeAnnotations: decodeAnnotations,
      decodeInfo: decodeInfo, openForm: openForm, listChunks: listChunks, parseSexp: parseSexp
    }
  };
});
